import { useEffect, useState } from 'react';

import { Pen } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { RootState } from '../../store';
import AppTable from '../../ui-components/AppTable';

import DynamicDropdownModal from './DynamicDropdownModal';

import { RouteParams } from './model';
import {
  useLazyToolsSettingsQuery,
  useUpdateSettingMutation,
} from './settingsApi';
import {
  setCompanySettings,
  setConfigSettings,
  setEmailSettings,
  setAuthenticateSettings,
  setSmsSettings,
  setConfigurationsSettings,
} from './settingsSlice';
import SubSideBar from './SubSideBar';

const SettingsPage = () => {
  const { category: name } = useParams<RouteParams>();
  const dispatch = useDispatch();

  const [modalOpen, setModalOpen] = useState(false);
  const [sendMode, setSendMode] = useState('');
  const [selectedSettingItem, setSelectedSettingItem] = useState<null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string>('');

  const [toolsSettingsApi, { isSuccess }] = useLazyToolsSettingsQuery();
  const [updateSetting] = useUpdateSettingMutation();
  const settings = useSelector((store: RootState) => store.settings);

  useEffect(() => {
    async function load() {
      if (!name) return;
      const type = name.toLowerCase();
      try {
        const settingsRes = await toolsSettingsApi({ type }).unwrap();
        const data = settingsRes?.data;
        if (data) {
          switch (type) {
            case 'company':
              dispatch(setCompanySettings(data));
              break;
            case 'config':
              dispatch(setConfigSettings(data));
              break;
            case 'email':
              dispatch(setEmailSettings(data));
              break;
            case 'authenticate':
              dispatch(setAuthenticateSettings(data));
              break;
            case 'sms':
              dispatch(setSmsSettings(data));
              break;
            case 'configurations':
              dispatch(setConfigurationsSettings(data));
              break;
            default:
              break;
          }

          const groupTypes = [
            ...new Set(data.map((item: any) => item.groupType as string)),
          ];
          setSelectedGroup((groupTypes[0] as string) || '');
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
    }
    load();
  }, [dispatch, name, toolsSettingsApi]);

  const typeKey = name?.toLowerCase();
  const dynamicSettings =
    typeKey && settings && typeKey in settings
      ? ((settings as any)[typeKey] as any[]) || []
      : [];

  const groupTypeArr = [
    ...new Set(dynamicSettings.map((item: any) => item.groupType)),
  ];

  const filteredSettings = dynamicSettings.filter(
    (item: any) => item.groupType === selectedGroup
  );

  const handleClick = (setting: any) => {
    setSelectedSettingItem(setting);
    setSendMode(setting.value);
    setModalOpen(true);
  };

  const handleModalSubmit = async () => {
    if (!selectedSettingItem) {
      return;
    }

    if (!sendMode) {
      console.warn('Please select a mode.');
      return;
    }

    try {
      const payload = {
        setting: {
          [(selectedSettingItem as any).name as string]: sendMode,
        },
      };

      await updateSetting(payload).unwrap();

      const updatedSettings = dynamicSettings.map((item: any) =>
        item.name === (selectedSettingItem as any).name
          ? { ...item, value: sendMode }
          : item
      );

      switch (typeKey) {
        case 'company':
          dispatch(setCompanySettings(updatedSettings));
          break;
        case 'config':
          dispatch(setConfigSettings(updatedSettings));
          break;
        case 'email':
          dispatch(setEmailSettings(updatedSettings));
          break;
        case 'authenticate':
          dispatch(setAuthenticateSettings(updatedSettings));
          break;
        case 'sms':
          dispatch(setSmsSettings(updatedSettings));
          break;
        case 'configurations':
          dispatch(setConfigurationsSettings(updatedSettings));
          break;
      }

      setModalOpen(false);
    } catch (error: unknown) {
      console.error(error);
    }
  };

  if (!isSuccess) {
    return <p> </p>;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex gap-6">
        <SubSideBar
          groupTypeArr={groupTypeArr}
          selectedGroup={selectedGroup}
          onSelect={setSelectedGroup}
          settings={filteredSettings}
        />
        <div className="flex-1 bg-white p-4 rounded-lg shadow-md">
          <AppTable
            data={filteredSettings}
            showAction={true}
            action={[
              <Pen
                key="pen-icon"
                size={15}
                className="text-brand-500 dark:text-white cursor-pointer"
              />,
            ]}
            handleEditClick={handleClick}
            showActionText="Action"
          />
        </div>
      </div>

      {selectedSettingItem && (
        <DynamicDropdownModal
          isOpen={modalOpen}
          title="App Settings"
          label={
            typeof (selectedSettingItem as any)?.name === 'string'
              ? (selectedSettingItem as any).name
              : ''
          }
          value={sendMode}
          options={
            Array.isArray((selectedSettingItem as any)?.sourceValue) &&
            (selectedSettingItem as any).sourceValue.length > 0
              ? (selectedSettingItem as any).sourceValue
              : [
                  {
                    name: (selectedSettingItem as any).value || '',
                    value: (selectedSettingItem as any).value || '',
                  },
                ]
          }
          onChange={setSendMode}
          onSubmit={handleModalSubmit}
          onClose={() => setModalOpen(false)}
          cancelText="Cancel"
          updateText="Update"
        />
      )}
    </div>
  );
};
export default SettingsPage;
